import { useEffect } from "react";

import { StudyContextData, SubmissionContextData } from "@/context/AppContext";
import { useStudyView } from "@/context/ViewContexts";

import { AppSurface } from "@shared/components/Surface";
import { RoundButton } from "@shared/components/Button";
import { Heading } from "@/components/study/Heading";

import { IdleFlow } from "@/views/study/components/submission/IdleFlow";
import { ValidatedFlow } from "@/views/study/components/submission/ValidatedFlow";
import { ClaudeManifestFlow } from "@/views/study/components/submission/ClaudeManifestFlow";
import { InvalidDataFlow } from "@/views/study/components/submission/InvalidDataFlow";
import { UploadErrorFlow } from "@/views/study/components/submission/UploadErrorFlow";

import { T } from "@/components/i18n";
import { UploadProgressAlert } from "@/components/submission/Alerts";

import { CircleFadingArrowUp } from "lucide-react";
import { isNonValidatedLinkedStudy } from "@shared/study";

interface SubmissionViewProps {
  studyContext: StudyContextData;
  submissionContext: SubmissionContextData;
}

export function SubmissionFlow({ submissionContext, studyContext }: SubmissionViewProps) {
  const { writeState } = submissionContext;

  switch (writeState.status) {
    case "idle":
      return <IdleFlow studyContext={studyContext} submissionContext={submissionContext} />;
    case "validated":
      return <ValidatedFlow studyContext={studyContext} submissionContext={submissionContext} />;
    case "found_claude_manifest":
      return (
        <ClaudeManifestFlow submissionContext={submissionContext} studyContext={studyContext} />
      );
    case "uploading":
      return <UploadProgressAlert progress={writeState.progress} />;
    case "validating":
      return null;
    case "uploaded":
      return null;
    case "file_error":
    case "validation_error":
      return <InvalidDataFlow submissionContext={submissionContext} studyContext={studyContext} />;
    case "upload_error":
      return <UploadErrorFlow submissionContext={submissionContext} studyContext={studyContext} />;
  }
}

export function SubmissionView({ studyContext, submissionContext }: SubmissionViewProps) {
  const { study } = studyContext;
  const { writeState, week, upload, validation, defaultWeek, overrideDefaultWeek } =
    submissionContext;
  const { setView: setStudyView } = useStudyView();

  useEffect(() => {
    if (writeState.status === "uploaded") {
      setStudyView("success");
    }
  }, [writeState.status]);

  const noValidation = studyContext.study && isNonValidatedLinkedStudy(studyContext.study);
  const rejected = validation?.status === "rejected" ? validation : null;
  const overridable = !!rejected?.failure.overridable;
  const unknownSafeFailure = overridable && rejected?.failure.source === "unknown";

  const weekMissing = (defaultWeek === null || overrideDefaultWeek) && week === null;

  const validationOk =
    writeState.status === "validated" || (overridable && !!rejected?.overrideValidation);

  const uploadDisabled =
    writeState.status === "uploading" ||
    !validationOk ||
    weekMissing ||
    (!!noValidation && unknownSafeFailure && rejected?.userSource === null);

  return (
    <div className="w-full max-w-2xl mt-12">
      <Heading />
      <div className="pt-4 mt-4 border-t border-neutral-200 dark:border-neutral-800">
        <p className="text-lg">
          <T i18nKey="view.submission.intro1" />
        </p>
      </div>
      <AppSurface className="mt-8">
        <h2 className="text-lg font-semibold flex items-center gap-x-1.5 mb-4">
          <CircleFadingArrowUp className="stroke-blue-500" size={20} strokeWidth={3} />
          <T i18nKey="view.submission.title" />
        </h2>
        <SubmissionFlow studyContext={studyContext} submissionContext={submissionContext} />
      </AppSurface>
      <div className="mt-6 mb-16 flex justify-between">
        <RoundButton
          onClick={() => {
            setStudyView("init");
          }}
          variant="secondary"
        >
          Back
        </RoundButton>
        <RoundButton
          disabled={uploadDisabled}
          onClick={() => {
            if (study) {
              upload();
            }
          }}
        >
          Upload file
        </RoundButton>
      </div>
    </div>
  );
}
